import {Controller, useFieldArray, useForm} from "react-hook-form";
import ControlledInput from "Components/Input";
import { useTranslation } from "react-i18next";
import Button from "Components/Button";
import Space from "Components/Space";
import FlexRow from "Components/FlexRow";
import React, {useEffect, useState} from "react";
import ControlledSelect from "../../Components/Select/ControlledSelect";
import Select from "../../Components/Select";
import useFilteredList from "../../Hooks/UseFilteredList";
import {useProjects} from "../../Apis/ProjectApi";
import {SettlementEntryDto} from "../../Apis/Models/SettlementSalesDto";
import {useDeleteSettlementEntry, useMutateSettlementEntry} from "../../Apis/SettlementEntryApi";
import DatePicker from "../../Components/DatePicker";
import moment from "moment/moment";
import styles from "../Customers/Components/CustomerForm.module.scss";
import Loader from "../../Assets/SVG/Loader";
import FileUpload from "../../Components/FileUpload/FileUpload";
import {IFileInformationDto} from "../../Apis/Models/IFileDto";
import {FeeReceiverSelect} from "./FeeReceiverSelect";
import XIcon from "../../Assets/SVG/XIcon";
import FormFieldBox from "../../Components/FormFieldBox";
import useToasts from "../../Hooks/UseToasts";
import {useSettlementSettingForProject} from "../../Apis/ProjectSettlementSettingsApi";

interface IProps {
    settlementEntry: Partial<SettlementEntryDto>;
    onSubmit?: () => void;
    currency: string;
}
const SettlementEntryForm = (props: IProps) => {

    const { t } = useTranslation();
    
    const { showSuccessToast, showErrorToast } = useToasts();

    const { data: projectSettlementSettings } = useSettlementSettingForProject(props.settlementEntry.projectId!)
    const { control, handleSubmit, reset, watch, setValue, getValues } = useForm<SettlementEntryDto>();
    const { fields: splitFields, append: addSplit, remove: removeSplit } = useFieldArray({ control, name: "splits"})
    const [file, setFile] = useState<IFileInformationDto>();

    const { mutateAsync, isLoading } = useMutateSettlementEntry();
    const { mutateAsync: deleteEntry, isLoading: deleteLoading } = useDeleteSettlementEntry();

    const { data: projects } = useProjects({includeAll: true})
    
    const [ filteredType, setFilteredType ] = useFilteredList(['Revenue', 'Expense'] ?? [], x => [x]);
    const [ filteredProjects, setFilteredProjects ] = useFilteredList(projects?.items ?? [], x => [x.name]);

    useEffect(() => {
        if (props.settlementEntry) {
            reset(props.settlementEntry);

            setFile(props.settlementEntry.uploadedFile);
        }
    }, [reset, props.settlementEntry])

    const onSubmit = async(data: SettlementEntryDto) => {
        await mutateAsync({ ...data, uploadedFile: file ?? undefined});

        if (props.onSubmit) {
            props.onSubmit();
        }

        reset();
    }
    
    const deleteSettlementEntry = async () => {
        await deleteEntry(props.settlementEntry as SettlementEntryDto);
        
        showSuccessToast(t('settlementEntryDeleted'));
        
        reset();

        if (props.onSubmit) {
            props.onSubmit();
        }    
    }

    const calculateSplitFromRevenueSplit = () => {
        const amount = getValues('amount');

        console.log(amount, projectSettlementSettings!.splits)

        if (!amount || amount <= 0) {
            showErrorToast(t('amountMustBeLagerThanZero'));
            return;
        }

        if (!projectSettlementSettings) {
            showErrorToast(t('projectSettlementSettingsNotFound'));
            return;
        }

        if (projectSettlementSettings.splits.length === 0) {
            showErrorToast(t('noSplitFound'));
            return;
        }

        setValue('splits', [])

        projectSettlementSettings.splits.forEach(split => {
            addSplit({name: split.name, amount: amount * (split.amount / 100)})
        })
    }
    
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Space direction={"vertical"} size={"large"}>
                <ControlledSelect control={control} placeholder={t('project')} name={"projectId"} onFilter={setFilteredProjects}>
                    {filteredProjects?.map(x => <Select.Option key={x.id} label={x.name} value={x.id} />)}
                </ControlledSelect>

                <Controller control={control} name={"date"} defaultValue={moment().format('YYYY-MM-DD')} render={({ field }) => (
                    <DatePicker name={field.name} onChange={field.onChange} value={field.value} allowClear={false} placeholder={t('date') ?? ''} />
                )} />                
                <ControlledInput rules={{required: true}} name={"text"} control={control} placeholder={t('text')} />

                <ControlledSelect control={control} placeholder={t('entryType')} name={"entryType"} onFilter={setFilteredType}>
                    {filteredType?.map(x => <Select.Option key={x} label={t(`settlementAccountType.${x}`)} value={x} />)}
                </ControlledSelect>

                <ControlledInput rules={{required: true}} name={"amount"} type={"number"} control={control} placeholder={t('amount')} suffix={props.currency} />

                {watch('entryType') === 'Expense' && (
                    <Space>
                        <h3 style={{margin: 0}}>
                            {t('entrySplit')} <span style={{fontSize: 12, cursor: 'pointer'}} onClick={calculateSplitFromRevenueSplit}>{t('calculateFromSplit')}</span>
                        </h3>
        
                        {splitFields.map((field, index) => (
                            <FormFieldBox key={field.id}>
                                <div className={"deletePositionAbsolute"} onClick={() => removeSplit(index)}>
                                    <XIcon />
                                </div>
                                <FeeReceiverSelect control={control} name={`splits.${index}.name`}></FeeReceiverSelect>
                                <ControlledInput name={`splits.${index}.amount`} type={"number"} control={control} placeholder={t('value')} suffix={"kr"} />
                            </FormFieldBox>
                        ))}
        
                        <Button buttonType={"button"} type={"secondary"} text={t('addFee')} onClick={() => addSplit({name: '', amount: 0})} />
                    </Space>
                )}

                <FileUpload callback={setFile} accept={['.jpeg', '.jpg', '.png', '.pdf']} render={loading => (
                    <div className={styles.fileUpload}>
                        {loading ? (<Loader color={"red"} height={50} width={50} />) : (<>
                            {!!file ? (
                                <div>
                                    {t('fileIsUploaded')}
                                </div>
                            ) : (
                                <>
                                    {t('upload')}
                                </>
                            )}
                        </>)}
                    </div>
                )} />

                <FlexRow justify={"space-between"}>
                    <div>
                        {props.settlementEntry?.id && (
                            <Button buttonType={"button"} loading={deleteLoading} type={"tertiary"} text={t('deleteEntry')} onClick={deleteSettlementEntry} />
                        )}
                    </div>
                    <Button buttonType={"submit"} type={"primary"} text={t('save')} loading={isLoading} />
                </FlexRow>
            </Space>
        </form>
    )
}

export default SettlementEntryForm
